import clsx from "clsx";

import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";
import type { ConversationParticipant } from "common/types";

import AudioPlayer from "../MessageField/AudioPlayer";
import Attachment from "./Attachment";
import LinkPreview from "./LinkPreview";
import MessageBubble from "./MessageBubble";
import TextMessage from "./TextMessage";

import type { ConversationMessage } from "../../../types";

interface MessageProps {
  accountId: string;
  message: ConversationMessage;
  participants?: ConversationParticipant[];
  setEditMessage: (message: ConversationMessage) => void;
  isLoading: boolean;
  showLeadPreview: (leadId: string) => void;
}

export default function Message({
  accountId,
  message,
  setEditMessage,
  participants = null,
  isLoading,
  showLeadPreview,
}: MessageProps) {
  const { id, permissions } = useSelectedWorkspaceContext();
  const { workspaceAccounts } = useWorkspaceAccounts(id);

  const isMine = isLoading
    ? !!Math.round(Math.random())
    : accountId === message.sender.id;

  const { deleted: isDeleted, body } = message;

  // Handle profile preview
  function handleProfilePreview(leadId: string) {
    showLeadPreview(leadId);
  }

  const messageComponents = [];

  if (isLoading) {
    let loaderBgColor = "bg-black-200";

    if (isDeleted) {
      loaderBgColor = "bg-red-200";
    } else if (isMine) {
      loaderBgColor = "bg-purple-200";
    }

    messageComponents.push(
      <MessageBubble
        participants={participants}
        isMine={isMine}
        message={message}
        accountId={accountId}
      >
        <div
          className={clsx([
            "flex flex-col gap-1.5",
            isMine ? "items-end justify-end" : "items-start justify-start",
          ])}
        >
          <Skeleton className={clsx(["h-3 w-48 rounded-3xl", loaderBgColor])} />
          <Skeleton className={clsx(["h-3 w-44 rounded-3xl", loaderBgColor])} />
          <Skeleton className={clsx(["h-3 w-36 rounded-3xl", loaderBgColor])} />
        </div>
      </MessageBubble>,
    );
  } else if (isDeleted) {
    messageComponents.push(
      <MessageBubble
        accountId={accountId}
        participants={participants}
        key="deleted-message"
        isMine={isMine}
        message={message}
        setEditMessage={() => setEditMessage(message)}
      >
        <span className="italic">This message has been deleted</span>
      </MessageBubble>,
    );
  } else if (
    "body" in message &&
    !body &&
    !("links" in message) &&
    !("audio" in message) &&
    !("attachments" in message)
  ) {
    //some message types are not supported yet, we display a specific message

    messageComponents.push(
      <MessageBubble
        accountId={accountId}
        participants={participants}
        key="invalid-message"
        isMine={isMine}
        message={message}
        setEditMessage={() => setEditMessage(message)}
      >
        <span className="italic">Unsupported message type</span>
      </MessageBubble>,
    );
  } else {
    if (body) {
      messageComponents.push(
        <MessageBubble
          accountId={accountId}
          participants={participants}
          key="message-body"
          isMine={isMine}
          message={message}
          setEditMessage={() => setEditMessage(message)}
        >
          <TextMessage message={message} />
        </MessageBubble>,
      );
    }

    if ("audio" in message) {
      const { duration, url } = message.audio;

      messageComponents.push(
        <MessageBubble
          accountId={accountId}
          participants={participants}
          key={`audio-message-${url}`}
          isMine={isMine}
          message={message}
          setEditMessage={() => setEditMessage(message)}
        >
          <AudioPlayer
            accountId={accountId}
            audioUrl={url}
            duration={duration}
          />
        </MessageBubble>,
      );
    } else {
      const { attachments, links } = message;

      if (attachments) {
        attachments.forEach((attachment) => {
          messageComponents.push(
            <div key={`attachment-container-${attachment.url}`}>
              <MessageBubble
                accountId={accountId}
                participants={participants}
                key={`message-bubble-${attachment.url}`}
                isMine={isMine}
                message={message}
                setEditMessage={() => setEditMessage(message)}
              >
                <Attachment
                  key={`attachment-content-${attachment.url}`}
                  attachment={attachment}
                  accountId={accountId}
                  isMine={isMine}
                />
              </MessageBubble>
            </div>,
          );
        });
      }

      if (links) {
        messageComponents.push(
          <LinkPreview
            isMine={isMine}
            key={`link-${links[0].url}`}
            link={links[0]}
          />,
        );
      }
    }
  }

  const imageSrc = isMine
    ? workspaceAccounts.find(({ id }) => id === message?.sender?.id)
        ?.picture_url
    : message?.sender?.picture_url;

  const profileImageComponent = (
    <ProfileImage
      size="sm"
      className="rounded-full"
      src={isLoading ? null : imageSrc}
    />
  );

  return (
    <div
      className={clsx(
        "my-1 flex gap-2 items-start",
        isMine ? "flex-row-reverse" : "flex-row",
      )}
      key={`message-container-${message.urn}`}
    >
      {isMine || !permissions.includes("leads_full") ? (
        profileImageComponent
      ) : (
        <button
          className="flex"
          type="button"
          onClick={() => handleProfilePreview(message?.sender?.id)}
          aria-label="view participant details"
        >
          {profileImageComponent}
        </button>
      )}
      <div className="flex flex-col gap-1">{messageComponents}</div>
    </div>
  );
}
